// What Nova can offer on each area of the app — short, genuinely useful pointers, never
// marketing. Keyed by route-path prefix; the longest matching prefix wins, and everything
// falls back to the global set. Keep each tip to one sentence a person can act on.

export interface NovaTip {
  text: string;
}

const GLOBAL: NovaTip[] = [
  { text: 'Press ⌘K (Ctrl-K) anywhere to search and run commands.' },
  { text: 'Right-click any list row for quick actions; tick rows to bulk-edit them.' },
  { text: 'Switch workspace skin, theme and density in Preferences.' },
];

// Longest prefix wins, so '/tickets/board' can override '/tickets'.
const BY_PREFIX: Record<string, NovaTip[]> = {
  '/tickets': [
    { text: 'Impact × urgency picks the priority automatically, and rules route the ticket.' },
    { text: 'Open the Board or Calendar view from the toolbar to work by status or due date.' },
    { text: 'SLA clocks pause and resume with the ticket’s status — watch the countdown chip.' },
  ],
  '/quotes': [
    { text: 'Send a quote to generate a public acceptance link for the customer.' },
    { text: 'An accepted quote converts straight to an invoice, contract, or project.' },
  ],
  '/invoices': [
    { text: 'Issuing freezes the numbers and posts to the ledger — it can’t be edited after.' },
    { text: 'To correct an issued invoice, raise a credit note or void it; never an edit.' },
  ],
  '/projects': [
    { text: 'Flip between Table, Board, Timeline and Workload from the tabs.' },
    { text: 'Budget-vs-time tracks logged hours against the plan as work lands.' },
  ],
  '/my-tasks': [{ text: 'Tasks due this week surface here and on Home; log time straight from a task.' }],
  '/timesheets': [
    { text: 'Log time against a ticket or task — the charge rate snapshots at the work date.' },
    { text: 'Locking a timesheet freezes its entries; invoicing pulls from the locked set.' },
  ],
  '/billing': [{ text: 'Unbilled billable time is gathered here, ready to turn into an invoice.' }],
  '/contracts': [{ text: 'Prepay balances draw down as time is logged; low balances flag on Home.' }],
  '/clients': [{ text: 'Companies carry sites and contacts; roles say who to bill and who to call.' }],
  '/opportunities': [{ text: 'Drag a card between pipeline stages, or use the move-stage action.' }],
  '/settings': [{ text: 'Roles and permissions here decide what each teammate can see and do.' }],
  '/mailboxes': [{ text: 'Connected mailboxes turn inbound email into tickets and thread replies.' }],
};

/** The tips for a path: the longest matching prefix, else the global set. */
export function tipsForPath(path: string): NovaTip[] {
  let best: { p: string; tips: NovaTip[] } | null = null;
  for (const [p, tips] of Object.entries(BY_PREFIX)) {
    if ((path === p || path.startsWith(p + '/') || path.startsWith(p)) && (!best || p.length > best.p.length)) {
      best = { p, tips };
    }
  }
  return best?.tips ?? GLOBAL;
}

/** A stable id for a path's first tip, so a one-time nudge is shown at most once per area. */
export function areaKey(path: string): string {
  for (const p of Object.keys(BY_PREFIX)) {
    if (path === p || path.startsWith(p + '/') || path.startsWith(p)) return p;
  }
  return 'global';
}
